import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { TravelBufferMockup } from '../components/visuals/TravelBufferMockup.js';
import { IconTrades } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-pool-care')({
  component: PoolCarePage,
});

function PoolCarePage() {
  usePageTitle('Bookings for pool cleaning & maintenance');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For pool cleaners',
  icon: IconTrades,
  headline: 'Keep the route tight. Skip the spreadsheet.',
  headlineEmphasis: 'route tight',
  subhead:
    'Bookaburra orders your fortnightly visits so the next-job suburb is always close to the last one, and reschedules quietly when a client changes plans.',
  Visual: TravelBufferMockup,
  business: {
    name: 'Crystal Clear Pools',
    location: 'Sydney North · 2 vans · 80 weekly clients',
    summary:
      'Picture a two-van pool cleaning business covering the Lower North Shore. Most clients are fortnightly residential; a handful are weekly commercial — a couple of gyms and a retirement home. The owner sits down on Sunday night to plan Monday’s route, suburb by suburb.',
  },
  dayTimeline: [
    {
      time: '7:00',
      event:
        'Van one starts the Mosman/Cremorne run. Van two starts the Lane Cove/Greenwich loop. Each van has 10–12 fortnightly stops.',
      bookaburraResponse:
        'Nothing to do. The travel-aware allocator clustered both routes by postcode when the bookings were made.',
    },
    {
      time: '8:30',
      event:
        'A new enquiry comes in from Coogee — out past the harbour, well off the patch.',
      bookaburraResponse:
        'Postcode whitelist rejects it before a slot is even shown. The picker says "we don’t service Coogee" and offers the contact form.',
    },
    {
      time: '10:15',
      event:
        'A regular tries to book a Wednesday clean. Wednesday is the eastern-suburbs run; she’s in Lane Cove.',
      bookaburraResponse:
        'Only offers days that fit her suburb’s route. Picker shows her Thursday and next Monday — Wednesday isn’t even an option.',
    },
    {
      time: '11:50',
      event:
        'Chlorine delivery runs three hours late. Twelve afternoon jobs aren’t viable.',
      bookaburraResponse:
        'Sick-day-style reshuffle picks up the twelve, drafts a move to tomorrow’s same-route window. Owner approves; one batch of "we’ll be a day late" SMS goes out.',
    },
    {
      time: '13:00',
      event:
        'A holiday-skip client opens her reminder and clicks reschedule — back for next fortnight, not this one.',
      bookaburraResponse:
        'Magic-link reschedule offers her route-compatible slots only. The skipped Monday slot opens up for the route allocator to absorb.',
    },
    {
      time: '14:30',
      event:
        'An ad-hoc green-pool clean enquiry comes in for tomorrow.',
      bookaburraResponse:
        'Picker only shows times where a van will already be in that postcode. Lands her on tomorrow’s 11:30, slotted between two existing Mosman jobs.',
    },
  ],
  pains: [
    {
      quote:
        'A client booked online "for Wednesday" and we drove an hour out of our way to fit them in.',
      explanation:
        'The booking page accepted any day the van was technically free. It had no idea Wednesday’s route was on the other side of the harbour.',
    },
    {
      quote:
        'Chlorine delivery ran late, so we had to ring twenty clients and push the whole day.',
      explanation:
        'One supplier delay turned into half a morning on the phone, with clients hearing a different story each time.',
    },
    {
      quote:
        'Half our fortnightlies were away for the school holidays and nobody mentioned it.',
      explanation:
        'We still drove the route. Empty driveways, locked side gates, no way to slot in the clients who actually wanted us that week.',
    },
  ],
  solutions: [
    {
      title: 'Only accept jobs in your patch',
      body: (
        <>
          A postcode whitelist limits the booking page to the suburbs your vans
          actually cover, so nobody from two hours away can lock in a slot.
          See{' '}
          <a
            href="/features#postcode-whitelist"
            className="text-brand-sage underline-offset-4 hover:underline"
          >
            postcode whitelist
          </a>
          .
        </>
      ),
    },
    {
      title: 'Offer slots that suit the route',
      body: (
        <>
          The travel-aware allocator only shows times that fit the day’s
          existing run, so a Mosman client can’t land in the middle of a
          Lane Cove afternoon.
        </>
      ),
    },
    {
      title: 'Move a whole day in one click',
      body: (
        <>
          When chlorine delivery is late, the sick-day reshuffle picks up the
          day’s jobs and offers each client the next sensible slot —
          one click, one batch of messages.
        </>
      ),
    },
    {
      title: 'Let clients move themselves',
      body: (
        <>
          A magic-link reschedule on every confirmation means the client who’s
          away for the holidays can shift their own visit without ringing the
          office.
        </>
      ),
    },
  ],
  industrySlug: 'pool-care',
};
